import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { reverse } from 'named-urls'

import { routes } from 'Routes'
import constants from 'constants/index'

const SOURCE_SHOW_PATHS = {
  ChangesManagement: (id) =>
    reverse(routes.changesManagement.show, { changesManagementId: id }),
  Complaint: (id) => reverse(routes.complaint.show, { complaintId: id }),
  ConsentForm: (id) => reverse(routes.consent.show, { consentFormId: id }),
  CookieBanner: (id) => reverse(routes.cookies.settings, { id }),
  DataProcess: (id) => reverse(routes.dataProcess.view, { dataProcessId: id }),
  DataProcessTreatment: (id) =>
    reverse(routes.dataProcess.treatment.manage, {
      dataProcessTreatmentId: id,
    }),
  GovernanceProject: (id) =>
    reverse(routes.governanceProjects.manage, { governanceProjectId: id }),
  Incident: (id) => reverse(routes.incidents.show, { incidentId: id }),
  MonitoringManagementKpi: (id) =>
    reverse(routes.kpi.show, { monitoringManagementKpiId: id }),
  MonitoringManagementPmc: (id) =>
    reverse(routes.pmc.show, { monitoringManagementPmcId: id }),
  PrivacyByDesign: (id) =>
    reverse(routes.privacyByDesign.show, { privacyByDesignId: id }),
  PrivacyPolicy: (id) => reverse(routes.documents.show, { documentId: id }),
  Questionnaire: (id) =>
    reverse(routes.dponetQuestionnaire.reply, { questionnaireId: id }),
  QuestionControl: (id) =>
    reverse(routes.questionControls.show, { questionControlId: id }),
  SolicitationTicket: (id) =>
    reverse(routes.technicalFaqs.tickets.details, { ticketId: id }),
  Ticket: (id) =>
    reverse(routes.privacyPortal.tickets.details, { ticketId: id }),
}

const useSourceOrigin = () => {
  const { SOURCE_ORIGIN_LABELS } = constants.activities

  const history = useHistory()

  const resolveOrigin = useCallback((task) => {
    // source_origin_* aponta o destino de navegação (ex.: card de plano de ação
    // → tratamento/medida-pai). Fallback para o sourceable quando ausente.
    // Resolve o par (type, id) de forma atômica: só usa a origem se AMBOS existirem,
    // evitando combinar o type de uma fonte com o id de outra.
    const hasOrigin = Boolean(task?.sourceOriginType && task?.sourceOriginId)
    const originType = hasOrigin ? task.sourceOriginType : task?.sourceableType
    const originId = hasOrigin ? task.sourceOriginId : task?.sourceableId
    return { originType, originId }
  }, [])

  const getSourceOriginPath = useCallback(
    (task) => {
      const { originType, originId } = resolveOrigin(task)
      if (!originType || !originId) return null
      const builder = SOURCE_SHOW_PATHS[originType]
      return builder ? builder(originId) : null
    },
    [resolveOrigin],
  )

  const getSourceOriginLabel = useCallback(
    (task) => {
      const { originType } = resolveOrigin(task)
      return originType ? SOURCE_ORIGIN_LABELS[originType] || null : null
    },
    [resolveOrigin],
  )

  const hasSourceOrigin = useCallback(
    (task) =>
      Boolean(getSourceOriginPath(task)) &&
      task?.sourceRemoved === false &&
      task?.sourceAccessible !== false,
    [getSourceOriginPath],
  )

  const openSourceOrigin = useCallback(
    (task) => {
      const path = getSourceOriginPath(task)
      if (path) history.push(path)
    },
    [getSourceOriginPath, history],
  )

  return {
    getSourceOriginPath,
    getSourceOriginLabel,
    hasSourceOrigin,
    openSourceOrigin,
  }
}

export default useSourceOrigin
